import type { Editor } from "@tiptap/vue-3";
import { type Ref, type ShallowRef, ref, watch } from "vue";

export interface EditableAreaStyleInputs {
    editor: ShallowRef<Editor | undefined>;
    editorContentElement: Ref<HTMLElement | null>;
    style: Ref<Record<string, string>>;
    backgroundColor: Ref<string>;
    backgroundImage: Ref<string | null>;
    padding: Ref<string>;
}

export function useEditableAreaStyle({
    editor,
    editorContentElement,
    style,
    backgroundColor,
    backgroundImage,
    padding,
}: EditableAreaStyleInputs) {
    const editableAreaElement = ref<HTMLElement | null>(null);

    const applyStyles = () => {
        if (!editableAreaElement.value) return;

        editableAreaElement.value.style.width = style.value.width ?? "auto";
        editableAreaElement.value.style.height = style.value.height ?? "auto";
        editableAreaElement.value.style.backgroundColor = backgroundColor.value;
        editableAreaElement.value.style.padding = padding.value;

        if (backgroundImage.value) {
            editableAreaElement.value.style.backgroundImage = `url(${backgroundImage.value})`;
            editableAreaElement.value.style.backgroundSize = "cover";
            editableAreaElement.value.style.backgroundRepeat = "no-repeat";
            editableAreaElement.value.style.backgroundPosition = "center";
        } else {
            editableAreaElement.value.style.backgroundImage = "";
        }
    };

    watch(editor, (editorInstance) => {
        if (!editorInstance) return;

        editorInstance.on("create", () => {
            const root = editorContentElement.value;
            if (!root) return;

            editableAreaElement.value = (root as any).rootEl.querySelector(".ProseMirror");
            applyStyles();
        });
    });

    watch(
        [style, backgroundColor, backgroundImage, padding],
        () => {
            applyStyles();
        },
        { deep: true }
    );

    return {
        editableAreaElement,
        applyStyles,
    };
}
